import React, { useEffect, useState } from "react";
import { Line, Bar } from "react-chartjs-2";
import {
  collection,
  query,
  getDocs,
  doc,
  deleteDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged, updateEmail, updatePassword } from "firebase/auth";
import { auth, db } from "../FirebaseConfig";
import "./Dashboard.css";
import "chart.js/auto";
import { useNavigate } from "react-router-dom";
import LeadsTable from "./leads/LeadsTable";
import triteklogo from "./triteklogo.png";
import OrdersTable from "./OrdersTable";
import DisputesTable from "./DisputesTable";

const AdminDashboard = () => {
  const [user, setUser] = useState({});
  const [users, setUsers] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [currentView, setCurrentView] = useState("dashboard");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const navigate = useNavigate()
  const [globalStats, setGlobalStats] = useState({
    totalLeads: 0,
    totalEarnings: 0,
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) { // Adjust breakpoint for mobile as needed
        setSidebarCollapsed(true);
      } else {
        setSidebarCollapsed(false);
      }
    };
  
    // Run on initial render
    handleResize();
  
    // Add resize event listener
    window.addEventListener("resize", handleResize);
  
    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        console.log("No user is logged in");
        setUser(null);
        navigate('/')
        return;
      }
  
      try {
        // Fetch current user data
        const userRef = collection(db, "influencers");
        const userQuery = query(userRef, where("uid", "==", currentUser.uid));
        const userSnapshot = await getDocs(userQuery);
  
        if (!userSnapshot.empty) {
          const currentUserData = userSnapshot.docs[0].data();
          setUser(currentUserData);
  
          // If admin, fetch additional data
          if (currentUserData.role === "admin") {
            await fetchAdminData();
          }else{
            navigate('/dashboard')
          }
        } else {
          console.warn("User not found in the influencers collection");
          setUser(null);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    });
  
    return () => unsubscribe(); // Cleanup subscription on unmount
  }, []);
  
  const fetchAdminData = async () => {
    try {
      // Fetch all influencers
      const allUsersSnapshot = await getDocs(collection(db, "influencers"));
      setUsers(
        allUsersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
      );
  
      // Fetch all payouts
      const allPayoutsSnapshot = await getDocs(collection(db, "payouts"));
      setPayouts(
        allPayoutsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
      );
  
      // Fetch all leads
      const leadsSnapshot = await getDocs(collection(db, "leads"));
      const leadData = leadsSnapshot.docs.map((doc) => doc.data());
      setLeads(leadData);
  
      // Calculate global stats
      let totalLeads = 0;
      let totalEarnings = 0;
  
      leadData.forEach((lead) => {
        totalLeads += 1;
        if (lead.status === "new") totalEarnings += 1;
        if (lead.status === "converted") totalEarnings += 2;
      });
  
      setGlobalStats({ totalLeads, totalEarnings });
    } catch (error) {
      console.error("Error fetching admin data:", error);
    }
  };
  const handleDeleteUser = async (userId) => {
    try {
      await deleteDoc(doc(db, "influencers", userId));
      setUsers((prev) => prev.filter((user) => user.id !== userId));
      alert("User deleted successfully!");
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  const handleAssignAdmin = async (userId) => {
    try {
      await updateDoc(doc(db, "influencers", userId), { role: "admin" });
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, role: "admin" } : user
        )
      );
      alert("Admin role assigned successfully!");
    } catch (error) {
      console.error("Error assigning admin role:", error);
    }
  };
  const handleUpdateUser = async (updatedInfo) => {
    const auth = getAuth();
    const userRef = doc(db, "influencers", user.id); // Assume user has an "id" field
  
    try {
      // Update Firestore user info
      await updateDoc(userRef, updatedInfo);
  
      // Update Auth email or password if necessary
      if (updatedInfo.email) {
        await updateEmail(auth.currentUser, updatedInfo.email);
      }
      if (updatedInfo.password) {
        await updatePassword(auth.currentUser, updatedInfo.password);
      }
  
      setUser((prev) => ({ ...prev, ...updatedInfo })); // Update local user state
      alert("Account updated successfully!");
    } catch (error) {
      console.error("Error updating user:", error);
      alert("Failed to update account. Please try again.");
    }
  };
  

  return (
    <div className={`dashboard-container ${sidebarCollapsed ? "collapsed" : ""}`} style={{background:'#F6F6FB'}}>
      {/* Sidebar */}
    
      <div className={`sidebar ${!sidebarCollapsed ? "open" : "collapsed"}`}>
      <img
        src={triteklogo}
        width={150}
        style={{margin:'2rem'}}
        />
        <ul>
          <li
            className={currentView === "dashboard" ? "active" : ""}
            onClick={() => setCurrentView("dashboard")}
          >
            Dashboard
          </li>
          <li
            className={currentView === "users" ? "active" : ""}
            onClick={() => setCurrentView("users")}
          >
            All Users
          </li>
          <li
            className={currentView === "disputes" ? "active" : ""}
            onClick={() => setCurrentView("disputes")}
          >
            Disputes
          </li>
          <li
            className={currentView === "settings" ? "active" : ""}
            onClick={() => setCurrentView("settings")}
          >
            Settings
          </li>
          <li
            className={currentView === "logout" ? "active" : ""}
            onClick={() =>{
                auth.signOut()
                navigate('/')
            }}
          >
            Logout
          </li>
        </ul>
      </div>

      {/* Main Content */}
      <div className="main-content">
      <button
          className="toggle-btn"
          onClick={() => setSidebarCollapsed((prev) => !prev)}
        >
          {sidebarCollapsed ? "☰" : "✖"}
        </button>
        {currentView === "dashboard" && (
        <OrdersTable/>
        )}
        {currentView === "disputes" && (
        <DisputesTable/>
        )}
        {currentView === "users" && (
        <>
        <h2>All Users</h2>
        <table className="admin-table">
        <thead>
        <tr>
        <th>Name</th>
        <th>Email</th>
        <th>Role</th>
        <th>Actions</th>
        </tr>
        </thead>
        <tbody>
        {users.map((user) => (
        <tr key={user.id}>
        <td>{user.name}</td>
        <td>{user.email}</td>
        <td>{user.role || "user"}</td>
        <td>
            <button
            className="delete-btn"
            onClick={() => {
                if (window.confirm(`Are you sure you want to delete ${user.name}'s account?`)) {
                handleDeleteUser(user.id);
                }
            }}
            >
            Delete
            </button>
            {user.role !== "admin" && (
            <button className="assign-admin-btn" onClick={() => handleAssignAdmin(user.id)}>
                Assign Admin
            </button>
            )}
        </td>
        </tr>
        ))}
        </tbody>
        </table>
        </>
        )}
        {currentView === "settings" && (
        <>
        {/* Settings View */}
        <div className="settings">
        <h2>Account Settings</h2>
        <form
        onSubmit={(e) => {
            e.preventDefault();
            const updatedInfo = {
            name: e.target.name.value,
            email: e.target.email.value,
            password: e.target.password.value,
            };
            handleUpdateUser(updatedInfo);
        }}
        >
        <div>
            <label>Name:</label>
            <input
            type="text"
            name="name"
            defaultValue={user.name || ""}
            />
        </div>
        <div>
            <label>Email:</label>
            <input
            type="email"
            name="email"
            defaultValue={user.email || ""}
            />
        </div>
        <div>
            <label>New Password:</label>
            <input type="password" name="password" />
        </div>
        <button type="submit">Update</button>
        </form>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
